import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { resolveTrainingSections } from '@/lib/trainingTokens'

export interface TrainingModule {
  id: string
  /**
   * The brokerage this copy belongs to (mig 304). Never null in anything this hook
   * returns — the generic master is filtered out — but it is carried because the
   * {{STATE}} substitution has to key off THIS module's org rather than the one
   * currently selected in the sidebar.
   */
  organization_id: string | null
  slug: string
  title: string
  version: number
  content: TrainingSection[]
  is_required: boolean
  created_at: string
}

export interface TrainingSection {
  id: string
  heading: string
  body: string
}

export interface TrainingCompletion {
  id: string
  module_id: string
  module_version: number
  acknowledged_at: string
}

interface TrainingStatus {
  /** All required modules the user still needs to complete */
  pendingModules: TrainingModule[]
  /** All completions for the current user */
  completions: TrainingCompletion[]
  /** True when all required modules at their current version are acknowledged */
  allComplete: boolean
  loading: boolean
  /**
   * The read FAILED and we do not know this user's training state.
   *
   * ⚠️ THIS IS A THIRD STATE AND IT HAS TO BE, because the two obvious ones are
   * both wrong. Before this existed the hook destructured only `{ data, isLoading }`
   * and never looked at `error`: a failed query left `data` undefined, so
   * `modules` fell back to `[]`, `pendingModules` was empty, `loading` was false —
   * and `allComplete` came out TRUE. A network blip or an RLS error waved the user
   * straight through mandatory compliance training, silently, with the gate
   * reporting success.
   *
   * The naive correction — force `allComplete` false on error — is the OTHER
   * failure, and it is the mig 201 lockout in a new coat: ProtectedRoute would
   * bounce to /onboarding/training, which reads the same failing query, renders no
   * modules, and can never be satisfied. Persistent error, permanent lockout.
   *
   * So the caller must distinguish "not finished" from "we could not find out".
   * `allComplete` is false here (never wave anyone through on a failed read) and
   * consumers branch on `error` to show a retry instead of routing on it.
   */
  error: Error | null
  refetch: () => void
}

// On TanStack Query with a stable per-user queryKey so EVERY consumer
// (ProtectedRoute gate + the TrainingOnboarding page) shares one cache.
// Before this was on useState, so the gate's instance and the page's instance
// held independent state — completing training in the page didn't update the
// gate, which re-redirected the user back to /onboarding/training.
export function useTrainingStatus(): TrainingStatus {
  const { user, activeOrg } = useAuth()
  const queryClient = useQueryClient()
  const userId = user?.id
  const orgId = activeOrg?.organization_id
  // orgId is IN the key: {{STATE}} is resolved inside this query (mig 221), so a
  // cached entry is state-specific. Without it, switching orgs would serve the
  // previous brokerage's state name in mandatory compliance copy.
  const queryKey = ['training-status', userId, orgId]

  // `error` is read on purpose — see the TrainingStatus.error doc comment. Dropping
  // it from this destructure is what made a failed read report allComplete = true.
  const { data, isLoading, error } = useQuery({
    queryKey,
    enabled: !!userId,
    queryFn: async () => {
      const [modulesResult, completionsResult] = await Promise.all([
        // The brokerage's OWN copy, never the generic master (mig 304). RLS already
        // scopes this to the caller's active orgs, so the filter below is belt-and-
        // braces — but it is the STATIC one on purpose.
        //
        // ⚠️ It is deliberately NOT `.eq('organization_id', orgId)`. activeOrg resolves
        // a beat after userId, so an org-valued filter would run with orgId undefined on
        // the first pass, return zero modules, and `allComplete` would go true — the
        // mig 201 vacuous pass, where an empty read waves a user straight past mandatory
        // compliance training. `.not(..., 'is', null)` cannot do that: it is the same
        // filter on every render regardless of what has loaded.
        supabase
          .from('training_modules')
          .select('id, organization_id, slug, title, version, content, is_required, created_at')
          .eq('is_required', true)
          .not('organization_id', 'is', null)
          // Deterministic order: the gate renders pendingModules[0], and "which one is
          // first" must not be whatever the planner returned this time.
          //
          // created_at ALONE DOES NOT ORDER THESE. Mig 304's backfill inserted every org's
          // copy in one statement and `default now()` is transaction-constant, so all of
          // them carry the identical timestamp. Without the unique tiebreaker a refetch can
          // resolve the tie the other way and remount the walkthrough on a different
          // brokerage's module mid-read.
          .order('created_at', { ascending: true })
          .order('id', { ascending: true }),
        supabase
          .from('training_completions')
          .select('id, module_id, module_version, acknowledged_at')
          .eq('user_id', userId!),
      ])

      // {{STATE}} is resolved HERE, at the data boundary, not at the render site
      // (mig 221). Doing it here means a future consumer of training content
      // cannot forget and ship a literal `{{STATE}}` to a licensed agent's screen.
      //
      // The tokens outlived the mig 304 split on purpose: each brokerage's copy was
      // seeded from the master VERBATIM, tokens and all, rather than being flattened
      // to whichever state happened to be live. So a brokerage that edits its copy
      // still gets its own state name, and the generic master stays sellable to a
      // brokerage in any state.
      //
      // The org may legitimately have no jurisdiction yet — nothing is set until
      // an admin picks one in Settings › Contract Dates — so resolveTrainingSections
      // falls back to "your state" rather than blanking compliance copy. A failed
      // lookup is treated the same way, deliberately: mandatory training must
      // render even if this query fails.
      //
      // ⚠️ RESOLVED PER MODULE, FROM THAT MODULE'S OWN ORG — NOT FROM activeOrg.
      // Before mig 304 there was one row and one org, so reading the state off activeOrg
      // was the same thing. It is not any more: this query returns a module for EVERY
      // brokerage the user is an active member of, and substituting the currently-selected
      // brokerage's state into another brokerage's copy would name the wrong state's law in
      // a document that person is about to legally acknowledge. The CODE is carried too,
      // not just the name: {{TITLE_PROFESSIONAL}} (mig 244) keys off the jurisdiction
      // itself, because whether an agent escalates a title question to a title company or
      // to an attorney is a fact about the state, not about how its name is spelled.
      // ⚠️ THROW, do not swallow. This is the whole of the fail-open fix, and reading
      // `error` off useQuery is NOT enough on its own — that was the first attempt and it
      // was a no-op.
      //
      // supabase-js does not reject on a query error. `shouldThrowOnError` defaults to
      // false, so a PostgREST 4xx/5xx, an RLS denial, a PGRST204 schema-cache miss after a
      // migration, or a network blip all RESOLVE as `{ data: null, error }`. The queryFn
      // then returned normally, useQuery's `error` stayed null, `modules` fell back to
      // `[]` via `?? []`, `pendingModules` was empty — and `allComplete` came out TRUE.
      // A failed read waved the user straight past mandatory compliance training.
      //
      // Throwing here is what actually populates useQuery's `error`, which is what
      // `allComplete`'s `!error` guard and ProtectedRoute's TrainingUnavailable branch
      // have always been reading. Without this line those two are unreachable code.
      const readError = modulesResult.error ?? completionsResult.error
      if (readError) {
        throw new Error(`training status read failed: ${readError.message}`)
      }

      const rawModules = (modulesResult.data ?? []) as unknown as TrainingModule[]
      const orgIds = [...new Set(rawModules.map(m => m.organization_id).filter((v): v is string => !!v))]

      const jurisdictionByOrg = new Map<string, { code: string | null; name: string | null }>()
      if (orgIds.length > 0) {
        const { data: orgRows } = await supabase
          .from('organizations')
          .select('id, contract_jurisdiction')
          .in('id', orgIds)
        const orgs = (orgRows ?? []) as { id: string; contract_jurisdiction: string | null }[]
        const codes = [...new Set(orgs.map(o => o.contract_jurisdiction).filter((c): c is string => !!c))]

        const nameByCode = new Map<string, string>()
        if (codes.length > 0) {
          const { data: jRows } = await supabase
            .from('jurisdictions')
            .select('code, name')
            .in('code', codes)
          for (const j of (jRows ?? []) as { code: string; name: string | null }[]) {
            if (j.name) nameByCode.set(j.code, j.name)
          }
        }
        for (const o of orgs) {
          jurisdictionByOrg.set(o.id, {
            code: o.contract_jurisdiction,
            name: o.contract_jurisdiction ? nameByCode.get(o.contract_jurisdiction) ?? null : null,
          })
        }
      }

      const modules = rawModules.map(m => {
        const j = m.organization_id ? jurisdictionByOrg.get(m.organization_id) : undefined
        return {
          ...m,
          content: resolveTrainingSections(m.content, j?.name ?? null, j?.code ?? null),
        }
      })

      return {
        modules,
        completions: (completionsResult.data ?? []) as TrainingCompletion[],
      }
    },
  })

  const modules = data?.modules ?? []
  const completions = data?.completions ?? []
  const loading = !!userId && isLoading

  const pendingModules = modules.filter(m =>
    completions.find(c => c.module_id === m.id && c.module_version === m.version) === undefined
  )

  return {
    pendingModules,
    completions,
    // Don't gate decisions until the query has actually resolved — and never on a
    // read that FAILED. `!error` is the whole of the compliance-bypass fix: without
    // it, a failed query produces empty modules, an empty pendingModules, and a
    // cheerful allComplete = true. An empty read is not a pass.
    allComplete: !loading && !error && pendingModules.length === 0,
    loading,
    error: (error as Error | null) ?? null,
    refetch: () => { void queryClient.invalidateQueries({ queryKey }) },
  }
}
